import {
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormReturn,
  UseFormTrigger,
} from 'react-hook-form';

import { DisclosableFeature } from '../../../redux/constants.ts';
import {
  ObjectiveCharacteristicsFormInput,
  ObjectiveCharacteristicsFormOutput,
} from '../schObjectiveCharacteristics.ts';
import { AreaInput } from './AreaInput.tsx';
import { FireplaceTypeSelect } from './FireplaceTypeSelect.tsx';
import { NumberInput } from './NumberInput.tsx';
import { PorchPatioDeckTypeSelect } from './PorchPatioDeckTypeSelect.tsx';
import { PriceInput } from './PriceInput.tsx';
import { SixTierQualityInput } from './SixTierQualityInput.tsx';
import { TextInput } from './TextInput.tsx';
import { ThreeTierQualityInput } from './ThreeTierQualityInput.tsx';

export function getFeatureInput(
  register: UseFormRegister<ObjectiveCharacteristicsFormInput>,
  trigger: UseFormTrigger<ObjectiveCharacteristicsFormInput>,
  errors: FieldErrors<ObjectiveCharacteristicsFormInput>,
  control: UseFormReturn<
    ObjectiveCharacteristicsFormInput,
    unknown,
    ObjectiveCharacteristicsFormOutput
  >['control'],
  feature: DisclosableFeature,
  includeCostApproach: boolean,
): React.ReactNode {
  switch (feature) {
    case 'garage':
      return (
        <>
          <Controller
            name="garage.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.garage?.area?.message}
              />
            )}
          />
          <Controller
            name="garage.spaces"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Spaces"
                errorMessage={errors.garage?.spaces?.message}
              />
            )}
          />
          <Controller
            name="garage.quality"
            control={control}
            render={({ field }) => <SixTierQualityInput label="Quality" required {...field} />}
          />
        </>
      );

    case 'detachedGarage':
      return (
        <>
          <Controller
            name="detachedGarage.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.detachedGarage?.area?.message}
              />
            )}
          />
          <Controller
            name="detachedGarage.spaces"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Spaces"
                errorMessage={errors.detachedGarage?.spaces?.message}
              />
            )}
          />
          <Controller
            name="detachedGarage.quality"
            control={control}
            render={({ field }) => <SixTierQualityInput label="Quality" required {...field} />}
          />
        </>
      );

    case 'carport':
      return (
        <>
          <Controller
            name="carport.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.carport?.area?.message}
              />
            )}
          />
          <Controller
            name="carport.spaces"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Spaces"
                errorMessage={errors.carport?.spaces?.message}
              />
            )}
          />
          <Controller
            name="carport.quality"
            control={control}
            render={({ field }) => <SixTierQualityInput label="Quality" required {...field} />}
          />
        </>
      );
    case 'basement':
      return (
        <>
          <Controller
            name="basement.totalArea"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Total Area"
                errorMessage={errors.basement?.totalArea?.message}
              />
            )}
          />
          <Controller
            name="basement.finishedArea"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Finished Area"
                errorMessage={errors.basement?.finishedArea?.message}
              />
            )}
          />
          <Controller
            name="basement.fullBaths"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Full Baths"
                errorMessage={errors.basement?.fullBaths?.message}
                onChange={(event) => {
                  field.onChange(event);
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  trigger('basement.totalArea');
                }}
              />
            )}
          />
          <Controller
            name="basement.halfBaths"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Half Baths"
                errorMessage={errors.basement?.halfBaths?.message}
                onChange={(event) => {
                  field.onChange(event);
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  trigger('basement.totalArea');
                }}
              />
            )}
          />
        </>
      );
    case 'attic':
      return (
        <>
          <Controller
            name="attic.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.attic?.area?.message}
              />
            )}
          />
          <Controller
            name="attic.quality"
            control={control}
            render={({ field }) => <SixTierQualityInput label="Quality" required {...field} />}
          />
        </>
      );
    case 'fireplace':
      return (
        <>
          <FireplaceTypeSelect register={register} />
          <Controller
            name="fireplace.count"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Count"
                required
                errorMessage={errors.fireplace?.count?.message}
              />
            )}
          />
        </>
      );
    case 'shed':
      return (
        <>
          <Controller
            name="shed.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                errorMessage={errors.shed?.area?.message}
                required
              />
            )}
          />

          <ThreeTierQualityInput {...register('shed.quality')} label="Quality" required />
        </>
      );
    case 'workshop':
      return (
        <>
          <Controller
            name="workshop.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.workshop?.area?.message}
              />
            )}
          />
          <ThreeTierQualityInput {...register('workshop.quality')} label="Workshop" required />
        </>
      );
    case 'barn':
      return (
        <>
          <Controller
            name="barn.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.barn?.area?.message}
              />
            )}
          />
          <ThreeTierQualityInput {...register('barn.quality')} label="Quality" required />
        </>
      );
    case 'pool':
      return (
        <>
          <Controller
            name="pool.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.pool?.area?.message}
              />
            )}
          />
          <ThreeTierQualityInput
            {...register('pool.quality')}
            label="Quality"
            required
            poolOrSpaQualityLabel
          />
        </>
      );
    case 'spa':
      return (
        <>
          <Controller
            name="spa.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.spa?.area?.message}
              />
            )}
          />
          <ThreeTierQualityInput
            {...register('spa.quality')}
            label="Quality"
            required
            poolOrSpaQualityLabel
          />
        </>
      );
    case 'adu':
      return (
        <>
          <Controller
            name="adu.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.adu?.area?.message}
              />
            )}
          />
          <Controller
            name="adu.quality"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Quality"
                required
                precision={1}
                min={1}
                max={6}
                errorMessage={errors.adu?.quality?.message}
              />
            )}
          />

          <Controller
            name="adu.fullBaths"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Full Baths"
                errorMessage={errors.adu?.fullBaths?.message}
              />
            )}
          />
          <Controller
            name="adu.halfBaths"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Half Baths"
                errorMessage={errors.adu?.halfBaths?.message}
              />
            )}
          />
        </>
      );
    case 'porch':
      return (
        <>
          <PorchPatioDeckTypeSelect register={register} name="porch.type" />

          <Controller
            name="porch.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.porch?.area?.message}
              />
            )}
          />
          <Controller
            name="porch.quality"
            control={control}
            render={({ field }) => <SixTierQualityInput label="Quality" required {...field} />}
          />
        </>
      );
    case 'patio':
      return (
        <>
          <PorchPatioDeckTypeSelect register={register} name="patio.type" />
          <Controller
            name="patio.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.patio?.area?.message}
              />
            )}
          />
          <Controller
            name="patio.quality"
            control={control}
            render={({ field }) => <SixTierQualityInput label="Quality" {...field} />}
          />
        </>
      );
    case 'deck':
      return (
        <>
          <PorchPatioDeckTypeSelect register={register} name="deck.type" />

          <Controller
            name="deck.area"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label="Area"
                required
                errorMessage={errors.deck?.area?.message}
              />
            )}
          />
          <Controller
            name="deck.quality"
            control={control}
            render={({ field }) => <SixTierQualityInput label="Quality" required {...field} />}
          />
        </>
      );
    case 'manualCost':
      return (
        <>
          <Controller
            name="manualCostDisplayName"
            control={control}
            render={({ field }) => <TextInput {...field} label="Display Name" />}
          />
          {includeCostApproach && (
            <Controller
              name="manualCost"
              control={control}
              render={({ field }) => (
                <PriceInput
                  {...field}
                  label="Cost"
                  required
                  errorMessage={errors.manualCost?.message}
                />
              )}
            />
          )}
        </>
      );

    default:
      return null;
  }
}
